/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import authorization from 'live/services/authorization'

import { IQueryParamsList } from 'plugin-aeapps-resources-manager/types'

import { hasAdminPermission, hasPermission } from '../permissions'
import { api, assetUrl } from './api.config'
import { IAssetExtensions } from './assetExtensions'
import { AssetType, getAssetType, getAssetTypesAsMap } from './assetType'

type IPostAssetBody = {
    basicInfo: {
        avatar: string
        assetCode: string
        affiliatedTo: number
        assetType: string
        description: string
        name: string
        responsiblePerson: string
    }
}

type IPatchAssetBody = {
    id: number
    assetExtensions: string[]
}

export interface IPaginatedList<T> {
    page: number
    perPage: number
    total: number
    data: T[]
}

export interface IAffiliatedIdAndName {
    affiliatedToId: number
    affiliatedToName: string
}

export interface IGeneralAsset {
    id: number
    basicInfo?: IBasicInfo
    assetExtensions: IAssetExtensions[]
    childGeneralAssets: IGeneralAsset[]
    productsUsingResource: string[]
}

export interface IBasicInfo {
    avatar: string
    assetCode: string
    name: string
    description?: string
    responsiblePerson?: string
    affiliatedTo?: number | IGeneralAsset
    affiliatedToName?: string
    assetType: string | AssetType
    assetTypeName?: string
    id: number
    generalAssetId: number
}

export const getAsset = async (assetId: number): Promise<IGeneralAsset> => {
    const queryString = new URLSearchParams({
        id: assetId?.toString() ?? ''
    }).toString()

    return fetch(
        hasAdminPermission
            ? `${assetUrl}${api.generalAssets.generalAssets}/admin/${assetId}?${queryString}`
            : `${assetUrl}${api.generalAssets.generalAssets}/${assetId}?${queryString}`,
        {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        }
    ).then(async fetchData => {
        if (fetchData.ok) {
            return fetchData.json().then(async (dto: IGeneralAsset) => {
                return fillGeneralAsset(dto)
            })
        } else if (fetchData.status === 404) {
            return undefined
        } else {
            throw new Error('There is a Problem while trying to connect with database')
        }
    })
}

export const getAssetChildren = async (id: number): Promise<IGeneralAsset[]> => {
    return fetch(`${assetUrl}${api.generalAssets.generalAssets}/${id}/children`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(async fetchData => {
        if (fetchData.ok) {
            return fetchData.json().then(async (generalAssets: IGeneralAsset[]) => {
                return generalAssets
            })
        } else if (fetchData.status === 404) {
            return undefined
        } else {
            throw new Error('There is a Problem while trying to connect with database')
        }
    })
}

export const getAffiliatedList = async (assetId?: number): Promise<IAffiliatedIdAndName[]> => {
    const queryString = new URLSearchParams({
        id: assetId?.toString() ?? ''
    }).toString()

    return fetch(
        hasAdminPermission
            ? `${assetUrl}${api.generalAssets.generalAssets}/affiliated/admin/?${queryString}`
            : `${assetUrl}${api.generalAssets.generalAssets}/affiliated/?${queryString}`,
        {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        }
    ).then(async fetchData => {
        if (fetchData.ok) {
            return fetchData.json().then(async (generalAssets: IAffiliatedIdAndName[]) => {
                return generalAssets
            })
        } else if (fetchData.status === 404) {
            return undefined
        } else {
            throw new Error('There is a Problem while trying to connect with database')
        }
    })
}

export const getAssets = async (
    params?: IQueryParamsList,
    page?: number,
    perPage?: number
): Promise<IPaginatedList<IGeneralAsset>> => {
    const queryString = new URLSearchParams({
        type: params?.type?.toString() ?? '',
        perPage: perPage?.toString() ?? '',
        page: page?.toString() ?? '',
        orderBy: params?.orderBy?.toString() ?? '',
        direction: params?.direction?.toString() ?? '',
        keyword: params?.keyword?.toString() ?? '',
        sortBy: params.orderBy,
        sortByDirection: params.direction,
        // id: params?.id?.toString() ?? '',
        filterByResourceName: params?.filterByResourceName?.toString() ?? '',
        filterByResourceCode: params?.filterByResourceCode?.toString() ?? '',
        filterByResourceDescription: params?.filterByResourceDescription?.toString() ?? '',
        filterByResourceType: params?.filterByResourceType?.toString() ?? '',
        filterByAffiliatedName: params?.filterByAffiliatedName?.toString() ?? '',
        filterByResponsible: params?.filterByResponsible?.toString() ?? ''
    }).toString()

    return fetch(
        hasAdminPermission
            ? `${assetUrl}${api.generalAssets.generalAssets}/admin/?${queryString}`
            : `${assetUrl}${api.generalAssets.generalAssets}/?${queryString}`,
        {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        }
    ).then(async fetchData => {
        if (fetchData.ok) {
            return fetchData.json().then(async (generalAssets: IPaginatedList<IGeneralAsset>) => {
                // return fillGeneralAssets(generalAssets)
                return generalAssets
            })
        } else if (fetchData.status === 404) {
            return undefined
        } else {
            throw new Error('There is a Problem while trying to connect with database')
        }
    })
}

export const getAssetsAsMap = async (): Promise<Map<number, IGeneralAsset>> => {
    return getAssets().then(objs =>
        objs?.data?.reduce((map, obj) => map.set(obj.id, obj), new Map<number, IGeneralAsset>())
    )
}
export const postAssets = async (body: IPostAssetBody): Promise<IGeneralAsset> => {
    return fetch(`${assetUrl}${api.generalAssets.generalAssets}/`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    })
        .then(async fetchData => {
            if (fetchData.ok) {
                return fetchData.json().then(async (generalAssets: IGeneralAsset) => {
                    return fillGeneralAsset(generalAssets)
                })
            } else if (fetchData.status === 500) {
                const { message } = await fetchData.json()
                throw new Error(message)
            } else {
                const { message } = await fetchData.json()
                throw new Error(message)
            }
        })
        .catch(err => {
            throw new Error(err)
        })
}

export const deleteAsset = async (id: number): Promise<any> => {
    return fetch(
        hasAdminPermission
            ? `${assetUrl}${api.generalAssets.generalAssets}/admin/${id.toString()}`
            : `${assetUrl}${api.generalAssets.generalAssets}/${id.toString()}`,
        {
            method: 'DELETE',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        }
    ).then(async fetchData => {
        let jsonData

        try {
            jsonData = await fetchData.json()
        } catch (err) {
            jsonData = undefined
        }

        if (fetchData.ok) {
            return jsonData
        }

        throw new Error(jsonData?.Message ?? jsonData?.error ?? jsonData?.message)
    })
}

export const patchAsset = async (body: IGeneralAsset): Promise<IGeneralAsset> => {
    const patchBody: IPatchAssetBody = {
        id: body.id,
        assetExtensions: body.assetExtensions.map(el => el.innerCode)
    }

    return fetch(`${assetUrl}${api.generalAssets.generalAssets}/${body.id}`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(patchBody)
    })
        .then(fetchData => {
            if (fetchData.ok) {
                return fetchData.json().then(async (generalAssets: IGeneralAsset) => {
                    return fillGeneralAsset(generalAssets)
                })
            } else {
                throw new Error(`{"status": ${fetchData.status} , "message": "${fetchData.statusText}"}`)
            }
        })
        .catch(err => {
            return JSON.parse(err.message)
        })
}

async function fillGeneralAsset(dto: IGeneralAsset) {
    const generalAsset = dto
    if (dto.basicInfo) {
        generalAsset.basicInfo.assetType = await getAssetType(generalAsset.basicInfo.assetType as string)

        if (generalAsset.basicInfo.affiliatedTo) {
            generalAsset.basicInfo.affiliatedTo = await getAsset(generalAsset.basicInfo.affiliatedTo as number)
        }
    }

    return generalAsset
}

// async function fillGeneralAssets(generalAssets: IPaginatedList<IGeneralAsset>) {
//     const generalAssetsMap = generalAssets?.data?.reduce(
//         (map, obj) => map.set(obj.id, obj),
//         new Map<number, IGeneralAsset>()
//     )
//     const types = await getAssetTypesAsMap()

//     generalAssets?.data?.forEach(asset => {
//         if (asset.basicInfo?.assetType) {
//             asset.basicInfo.assetType = types.get(asset.basicInfo.assetType as string)
//         }
//         if (asset.basicInfo?.affiliatedTo) {
//             const parentAssetId = asset.basicInfo.affiliatedTo as number
//             const parentAsset = generalAssetsMap.get(parentAssetId)
//             if (!parentAsset?.childGeneralAssets) {
//                 parentAsset.childGeneralAssets = []
//             }
//             parentAsset.childGeneralAssets = [...parentAsset.childGeneralAssets, asset]
//         }

//         return asset
//     })

//     return generalAssets
// }
